/**
 * One client's profile — the legacy's `/organization/<id>`, in our vocabulary.
 *
 * Reached as `/clients/:id` (ND-C1, resolved 2026-09-07: **URL scope**, not a
 * persisted "current client"). The id in the URL is the source of truth, which
 * is why this screen has to survive being handed an id that is not ours: a
 * cross-tenant read answers `NOT_FOUND`, never `FORBIDDEN` (constitution §3),
 * and that must render as an answer rather than as a crash.
 *
 * Editing goes through the service's `ClientPatch`, which accepts three fields.
 * GSTIN, PAN, state and status are shown and not editable — the GSTIN is the
 * record's identity, and status moves through archive so the metered count
 * stays honest.
 */

import { Alert, Button, Card, Input, buttonClass } from '@fiveox/ui'
import { Archive, ArrowLeft, Building2, Pencil } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'

import { CLIENT, useEvaluation } from '../lib/access'
import { ApiError, type ClientRecord, clients } from '../lib/api'
import { EDIT_GROUPS, profileGroups, toPatch } from '../lib/clientProfile'
import { TurnoverCard } from './TurnoverCard'

export function ClientProfile() {
  const { id = '' } = useParams()
  const { can } = useEvaluation()

  const [client, setClient] = useState<ClientRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [missing, setMissing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [confirmArchive, setConfirmArchive] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setMissing(false)
    try {
      setClient((await clients.get(id)).data)
    } catch (err) {
      // A client of another firm is NOT_FOUND by design. Render it as the
      // answer it is; anything else leaks that the record exists elsewhere.
      if (err instanceof ApiError && err.code === 'NOT_FOUND') setMissing(true)
      else setError(err instanceof ApiError ? err.message : 'That client could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  const startEdit = useCallback(() => {
    if (!client) return
    const next: Record<string, string> = {}
    for (const group of EDIT_GROUPS) {
      for (const f of group.fields) {
        next[f.key] = String((client as unknown as Record<string, unknown>)[f.key] ?? '')
      }
    }
    setDraft(next)
    setError(null)
    setEditing(true)
  }, [client])

  const save = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      await clients.update(id, toPatch(draft))
      // **Re-READ rather than trusting the PATCH response.** `_out` omits PAN;
      // only the single read adds it back, and only for a caller holding
      // `can_update`. Rendering the patch response therefore makes a PAN the
      // record *does* hold vanish from the screen — which reads as "no PAN on
      // file". Found by saving the form and watching the field disappear
      // (2026-09-07); the shape of a write response is not the shape of a read.
      setClient((await clients.get(id)).data)
      setEditing(false)
    } catch (err) {
      // The service names its refusal. Show that, not "something went wrong".
      setError(err instanceof ApiError ? err.message : 'That change could not be saved.')
    } finally {
      setSaving(false)
    }
  }, [draft, id])

  const archive = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      setClient((await clients.archive(id)).data)
      setConfirmArchive(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That client could not be archived.')
    } finally {
      setSaving(false)
    }
  }, [id])

  if (loading) return <p className="gst-sub">Loading…</p>

  if (missing) {
    return (
      <div>
        <Link to="/clients" className={buttonClass({ variant: 'outline', size: 'sm', className: 'mb-4' })}>
          <ArrowLeft size={15} /> All clients
        </Link>
        <div className="gst-empty">
          <p className="mb-2 font-medium">No such client</p>
          <p>It may have been removed, or it belongs to another firm.</p>
        </div>
      </div>
    )
  }

  if (!client) {
    return (
      <div>
        <Link to="/clients" className={buttonClass({ variant: 'outline', size: 'sm', className: 'mb-4' })}>
          <ArrowLeft size={15} /> All clients
        </Link>
        <Alert tone="error">{error ?? 'That client could not be loaded.'}</Alert>
      </div>
    )
  }

  const groups = profileGroups(client)
  // Identity stays on screen while the rest is being edited. Hiding the GSTIN
  // and PAN behind an edit mode means the practitioner cannot see WHICH record
  // they are renaming at the moment they rename it.
  const identity = groups.filter((g) => g.title === 'Identity')
  const rest = groups.filter((g) => g.title !== 'Identity')
  const editable = can(CLIENT, 'can_update')

  const readOnlyCard = (g: (typeof groups)[number]) => (
    <section key={g.title} className="gst-card p-4">
      <p className="gst-panel-h mb-3">{g.title}</p>
      <dl className="grid gap-2">
        {g.fields.map((f) => (
          <div key={f.key} className="flex items-baseline justify-between gap-3">
            <dt className="gst-meta">{f.label}</dt>
            <dd className={f.mono ? 'gst-mono' : undefined}>{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/clients" className="gst-meta mb-2 inline-flex items-center gap-1">
            <ArrowLeft size={14} /> All clients
          </Link>
          <h1 className="gst-h1">
            <Building2 size={20} /> {client.legal_name}
          </h1>
          <p className="gst-sub">
            <span className="gst-mono">{client.gstin}</span>
            {client.status === 'archived' && <span className="gst-chip ml-2">Archived</span>}
          </p>
        </div>

        {!editing && (
          <div className="flex gap-2">
            {can(CLIENT, 'can_delete') && client.status === 'active' && (
              <Button type="button" onClick={() => setConfirmArchive(true)} variant="outline" size="sm">
                <Archive size={15} /> Archive
              </Button>
            )}
            {editable && (
              <Button type="button" onClick={startEdit} size="sm">
                <Pencil size={15} /> Edit details
              </Button>
            )}
          </div>
        )}
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      {confirmArchive && (
        <Alert tone="warning" className="mb-4">
          <p className="gst-panel-h">Archive this client?</p>
          <p className="mb-3 text-sm">
            The client stops counting towards the plan and leaves the working list. Nothing
            already prepared or filed is deleted, and the client can be onboarded again with
            the same GSTIN.
          </p>
          <div className="flex gap-2">
            <Button type="button" size="sm" disabled={saving} onClick={() => void archive()}>
              {saving ? 'Archiving…' : 'Archive client'}
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => setConfirmArchive(false)}>
              Keep it
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {identity.map(readOnlyCard)}

        {editing ? (
        <Card className="p-4">
          {EDIT_GROUPS.map((group) => (
            <div key={group.title} className="mb-4">
              <p className="gst-panel-h mb-3">{group.title}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {group.fields.map((f) => (
                  <Input
                    key={f.key}
                    label={f.label}
                    value={draft[f.key] ?? ''}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                    }
                  />
                ))}
              </div>
            </div>
          ))}
          <p className="gst-meta mt-3">
            GSTIN, PAN, state and status are not editable here — the GSTIN is this
            record&rsquo;s identity, and status changes by archiving.
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={saving || !draft.legal_name?.trim()}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </Card>
        ) : (
          rest.map(readOnlyCard)
        )}

        <TurnoverCard clientId={id} pan={client.pan ?? null} />
      </div>
    </div>
  )
}
